import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import Logo from '../../assets/logo.png';
import { useGloriaFood } from '../../hooks/useGloriaFood';
import useSiteData from '../../hooks/useSiteData';
import OrderButton from '../ui/OrderButton';

const Navbar = () => {
  const { data, isLoading } = useSiteData();
  useGloriaFood();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    useEffect(() => {
      console.log('Navbar Data:', data);
      console.log('Loading State:', isLoading);
    }, [data, isLoading]);
  }

  // Memoize nav links to prevent unnecessary re-renders
  const navLinks = React.useMemo(() => [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Menu', path: '/menu' },
    { name: 'Contact', path: '/contact' },
    { name: 'Catering', path: '/catering' },
  ], []);

  const renderSocialLinks = () => {
    // Remove the early return to see if social media data is coming through
    const socialMedia = data?.business?.socialMedia;
    console.log('Social Media Data:', socialMedia);

    // Define social media platforms in specific order with their brand colors
    const socialPlatforms = [
      {
        id: 'facebook',
        hoverColor: 'hover:text-[#1877F2]', // Facebook blue
        icon: (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
          </svg>
        )
      },
      {
        id: 'instagram',
        hoverColor: 'hover:text-[#E4405F]', // Instagram pink
        icon: (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
          </svg>
        )
      },
      {
        id: 'yelp',
        hoverColor: 'hover:text-[#FF1A1A]', // Yelp red
        icon: (
          <svg className='h-5 w-5' fill="currentColor"  viewBox="0 0 32 32">
            <path d="M11.876,19.928l1.381-.319c.029-.007,.077-.018,.136-.037,.381-.102,.708-.349,.911-.688,.203-.339,.267-.743,.179-1.128-.002-.008-.004-.016-.006-.025-.045-.184-.125-.359-.234-.514-.155-.196-.346-.361-.564-.484-.256-.146-.524-.27-.801-.371l-1.515-.553c-.85-.316-1.7-.624-2.558-.925-.556-.198-1.028-.372-1.437-.498-.077-.024-.163-.048-.232-.072-.495-.152-.843-.215-1.138-.217-.197-.007-.393,.029-.574,.105-.189,.082-.359,.202-.499,.354-.069,.079-.135,.161-.195,.247-.057,.087-.109,.178-.157,.271-.052,.1-.097,.203-.134,.309-.281,.831-.421,1.703-.414,2.58,.005,.793,.026,1.811,.462,2.501,.105,.177,.246,.33,.414,.449,.31,.214,.624,.243,.951,.266,.488,.035,.96-.085,1.432-.194l4.589-1.06h.004Z"></path><path d="M27.288,12.594c-.377-.792-.884-1.514-1.5-2.138-.08-.079-.165-.152-.255-.219-.083-.063-.17-.121-.26-.174-.092-.05-.187-.096-.284-.136-.192-.075-.397-.109-.602-.1-.197,.011-.388,.065-.562,.158-.263,.131-.548,.341-.928,.694-.052,.052-.118,.111-.177,.166-.313,.294-.661,.657-1.075,1.079-.64,.647-1.27,1.297-1.896,1.954l-1.12,1.162c-.205,.212-.392,.442-.558,.685-.142,.206-.242,.438-.295,.682-.031,.187-.026,.379,.013,.565,.002,.008,.004,.016,.006,.025,.088,.385,.323,.72,.653,.935,.331,.215,.732,.294,1.12,.219,.061-.009,.109-.019,.138-.027l5.973-1.38c.471-.109,.948-.208,1.371-.454,.284-.164,.553-.328,.738-.656,.099-.181,.159-.38,.176-.585,.092-.814-.333-1.738-.675-2.454Z"></path><path d="M16.598,15.106c.433-.544,.432-1.354,.47-2.017,.13-2.214,.267-4.428,.376-6.643,.042-.839,.132-1.667,.082-2.512-.041-.697-.046-1.498-.486-2.07-.777-1.009-2.435-.926-3.565-.77-.346,.048-.693,.113-1.038,.195s-.685,.173-1.018,.28c-1.085,.355-2.61,1.008-2.869,2.258-.146,.707,.199,1.43,.467,2.075,.324,.782,.768,1.486,1.172,2.222,1.068,1.943,2.156,3.874,3.24,5.807,.324,.577,.677,1.308,1.304,1.607,.041,.018,.084,.034,.127,.048,.281,.106,.587,.127,.88,.059,.018-.004,.035-.008,.052-.012,.27-.074,.515-.221,.707-.425,.035-.033,.068-.067,.099-.103Z"></path><path d="M16.079,21.033c-.169-.238-.409-.416-.685-.509-.276-.093-.575-.096-.853-.009-.065,.022-.129,.048-.191,.079-.095,.048-.186,.105-.271,.169-.249,.184-.459,.424-.649,.664-.048,.061-.093,.143-.15,.195l-.961,1.321c-.545,.74-1.082,1.483-1.614,2.237-.347,.487-.648,.898-.885,1.261-.045,.069-.092,.145-.135,.206-.285,.44-.446,.761-.528,1.048-.062,.19-.082,.392-.058,.59,.026,.207,.096,.406,.204,.584,.058,.089,.12,.176,.186,.259,.069,.08,.142,.156,.219,.228,.082,.079,.17,.151,.264,.216,.663,.461,1.389,.793,2.153,1.049,.635,.211,1.296,.337,1.964,.375,.114,.006,.228,.003,.341-.007,.105-.009,.21-.024,.313-.044,.104-.024,.206-.054,.306-.089,.195-.073,.373-.186,.522-.332,.141-.141,.249-.311,.318-.498,.111-.277,.184-.628,.232-1.15,.004-.074,.015-.163,.022-.245,.038-.433,.055-.942,.083-1.539,.047-.92,.084-1.835,.113-2.753,0,0,.062-1.632,.062-1.632,.014-.376,.003-.793-.102-1.167-.046-.179-.12-.35-.219-.507h0Z"></path><path d="M26.924,23.587c-.2-.22-.485-.439-.933-.71-.065-.036-.14-.084-.21-.126-.373-.224-.823-.46-1.348-.746-.807-.443-1.614-.875-2.428-1.303l-1.44-.763c-.075-.022-.15-.076-.221-.11-.277-.132-.57-.256-.874-.312-.105-.02-.211-.031-.318-.033-.069,0-.138,.003-.206,.012-.288,.045-.554,.179-.761,.384-.207,.205-.344,.47-.392,.758-.022,.183-.015,.368,.02,.549,.07,.383,.241,.762,.418,1.094l.769,1.441c.428,.813,.861,1.62,1.304,2.426,.286,.525,.525,.975,.748,1.347,.042,.07,.09,.146,.126,.21,.272,.448,.49,.73,.711,.932,.143,.138,.315,.244,.503,.309,.198,.066,.407,.089,.615,.068,.105-.012,.21-.031,.314-.054,.102-.027,.203-.06,.301-.098,.106-.04,.21-.088,.31-.143,.584-.328,1.123-.731,1.602-1.199,.575-.565,1.082-1.182,1.477-1.888,.055-.1,.102-.204,.142-.31,.037-.099,.069-.199,.096-.301,.023-.104,.041-.209,.054-.314,.02-.207-.004-.416-.071-.614-.065-.189-.171-.362-.31-.505h0Z"/>
          </svg>
        )
      },
      {
        id: 'tripAdvisor',
        hoverColor: 'hover:text-green-700', // TripAdvisor green
        icon: (
          <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12.006 4.295c-2.67 0-5.338.467-7.276 1.395C3.386 6.323 2.05 7.164 2.05 7.164L2 7.19v.032c0 .75.614 1.365 1.365 1.365.307 0 .593-.103.82-.277.076.102.15.205.224.31 1.55 2.193 4.083 3.636 6.963 3.636 2.88 0 5.412-1.443 6.963-3.636.074-.105.148-.208.224-.31.227.174.513.277.82.277.751 0 1.365-.615 1.365-1.365v-.032l-.05-.026s-1.336-.84-2.694-1.474c-1.938-.928-4.606-1.395-7.276-1.395v.001zm0 2.726c1.82 0 3.297 1.476 3.297 3.296 0 1.82-1.477 3.297-3.297 3.297-1.82 0-3.297-1.477-3.297-3.297 0-1.82 1.477-3.296 3.297-3.296zm0 .973c-1.28 0-2.323 1.043-2.323 2.323s1.043 2.323 2.323 2.323 2.323-1.043 2.323-2.323-1.043-2.323-2.323-2.323zm-6.314.014c1.82 0 3.297 1.476 3.297 3.296 0 1.82-1.477 3.297-3.297 3.297-1.82 0-3.297-1.477-3.297-3.297 0-1.82 1.477-3.296 3.297-3.296zm12.628 0c1.82 0 3.297 1.476 3.297 3.296 0 1.82-1.477 3.297-3.297 3.297-1.82 0-3.297-1.477-3.297-3.297 0-1.82 1.477-3.296 3.297-3.296zm-12.628.973c-1.28 0-2.323 1.043-2.323 2.323s1.043 2.323 2.323 2.323 2.323-1.043 2.323-2.323-1.043-2.323-2.323-2.323zm12.628 0c-1.28 0-2.323 1.043-2.323 2.323s1.043 2.323 2.323 2.323 2.323-1.043 2.323-2.323-1.043-2.323-2.323-2.323z"/>
          </svg>
        )
      }
    ];

    return socialPlatforms.map(platform => {
      const socialData = socialMedia?.[platform.id];
      if (socialData?.enable && socialData?.url) {
        return (
          <a
            key={platform.id}
            href={socialData.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-gray-600 transition-colors duration-200 ${platform.hoverColor} w-5 h-5`}
            aria-label={`Visit our ${platform.id} page`}
          >
            {platform.icon}
          </a>
        );
      }
      return null;
    });
  };

  // Optimized loading state with fixed dimensions
  if (isLoading && !data) {
    return (
      <nav className="bg-white shadow-md relative h-20">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <div className="w-[64px] h-[64px] bg-gray-200 rounded"></div>
            <div className="hidden md:flex space-x-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-20 h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <div className="flex items-center space-x-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-5 h-5 bg-gray-200 rounded-full"></div>
                ))}
              </div>
              <div className="w-24 h-10 bg-gray-200 rounded-lg"></div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-gray-100 shadow-lg relative h-20 border-b border-gray-100">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo with fixed dimensions and placeholder */}
          <div className="flex-shrink-0 w-[72px] h-[72px] relative">
            <Link to="/" className="block w-full h-full relative group">
              <div className="absolute inset-0 bg-gray-200 rounded-full" /> {/* Placeholder */}
              <img
                src={data?.business?.logoUrl || Logo}
                alt={data?.business?.businessName || 'Restaurant'}
                className="w-full h-full object-contain relative z-10"
                width={72}
                height={72}
                loading="eager"
                decoding="async"
                onLoad={(e) => {
                  e.target.previousSibling.style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-primary/5 rounded-full scale-0 group-hover:scale-100 transition-transform duration-300" />
            </Link>
          </div>

          {/* Mobile menu button with animation */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-full text-gray-700 hover:text-primary hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-300"
              aria-expanded={isMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              <div className="relative w-6 h-6">
                <span className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${isMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'}`} />
                <span className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`} />
                <span className={`absolute block h-0.5 w-6 bg-current transform transition-all duration-300 ease-in-out ${isMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'}`} />
              </div>
            </button>
          </div>

          {/* Desktop Navigation Links with hover effects */}
          <div className="hidden md:flex space-x-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.path}
                className={({ isActive }) =>
                  `relative text-base font-semibold transition-colors duration-300 px-2 py-1 
                  ${isActive 
                    ? 'text-primary' 
                    : 'text-gray-700 hover:text-primary'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.name}
                    <span 
                      className={`absolute bottom-0 left-0 w-full h-0.5 bg-primary transform origin-left transition-transform duration-300 
                      ${isActive ? 'scale-x-100' : 'scale-x-0'} group-hover:scale-x-100`} 
                    />
                  </>
                )}
              </NavLink>
            ))}
          </div>

          {/* Desktop Right Side with improved social icons */}
          <div className="hidden md:flex items-center space-x-8">
            <div className="flex items-center space-x-6">
              {renderSocialLinks()}
            </div>
            <OrderButton 
              className="bg-primary text-white px-8 py-2.5 rounded-full text-sm font-semibold 
                transition-all duration-300 transform hover:scale-105 hover:shadow-lg 
                hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary 
                focus:ring-offset-2 active:scale-95"
            >
              Order Now
            </OrderButton>
          </div>
        </div>
      </div>

      {/* Mobile menu with improved animation and styling */}
      <div
        className={`${
          isMenuOpen ? 'max-h-[450px] opacity-100' : 'max-h-0 opacity-0'
        } md:hidden absolute top-20 left-0 right-0 bg-white shadow-xl z-50 overflow-hidden 
        transition-all duration-500 ease-in-out border-b border-gray-100`}
      >
        <div className="px-4 py-3 space-y-2">
          {navLinks.map((link) => (
            <NavLink
              key={link.name}
              to={link.path}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg text-base font-medium transition-all duration-300
                ${isActive
                  ? 'text-primary bg-primary/5 shadow-sm'
                  : 'text-gray-700 hover:text-primary hover:bg-gray-50'
                }`
              }
              onClick={() => setIsMenuOpen(false)}
            >
              {link.name}
            </NavLink>
          ))}
          
          {/* Mobile Social Links with improved layout */}
          <div className="flex items-center space-x-6 px-4 py-4 border-t border-gray-100 mt-4">
            {renderSocialLinks()}
          </div>
          
          {/* Mobile CTA with matching style */}
          <div className="px-4 py-4">
            <OrderButton 
              className="w-full bg-primary text-white py-3 rounded-lg text-base font-semibold
                transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg
                hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary
                focus:ring-offset-2 active:scale-[0.98]"
            >
              Order Now
            </OrderButton>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
